import { useState } from 'react'
import { alpha } from '@mui/material'
import { isAxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { AddressBookIcon, DeleteIcon, KeyIcon, UnlinkIcon } from '@dhl-ui/icons'
import { Box, Button, Card, Grid } from '@dhl-ui/components'
import { useNotificationMessage } from '~/common'
import { UsersDangerZoneDialog, type ActionType } from '~/modules/users/components'
import { DangerZoneContainer } from '~/modules/users/components/DangerZoneContainer'
import { getActiveApiKeys, type User } from '~/modules/users/model'

type Props = {
  user: User
  onUserRemoved?: (email: string) => void
  onUserDisconnected?: (email: string) => void
}

export const DangerZoneCard = ({ user, onUserRemoved, onUserDisconnected }: Props) => {
  const { userType } = user
  const { t } = useTranslation()
  const { showError } = useNotificationMessage()
  const [dangerZoneActionType, setDangerZoneActionType] = useState<ActionType | undefined>()
  const [getWarningMessage, setWarningMessage] = useState<string | undefined>()
  const isAccountManager = userType === 'accountManager'

  const disconnectUser = async () => {
    try {
      const response = await getActiveApiKeys(user.id)

      if (response && response.hasActiveKeys) {
        const warningMessage = t('admin.usersPage.dangerZoneView.activeApiKeysWarning')
        setWarningMessage(warningMessage)
      }
      setDangerZoneActionType('disconnectUser')
    } catch (error) {
      if (isAxiosError(error)) {
        showError(error.message)
      }
    }
  }

  return (
    <>
      <Card>
        {/* TODO-DS: background color Card 'danger', error with gradient */}
        <Box
          sx={({ palette: { error, common } }) => ({
            background: `linear-gradient(0deg, ${alpha(error.main, 0.1)}, ${alpha(error.main, 0.1)}), ${common.white}`,
          })}
        >
          <Card.Header title={t('admin.usersPage.dangerZoneView.titleText')} />
          <Card.Content>
            <Grid container sx={{ alignContent: 'flex-start' }}>
              <DangerZoneContainer
                key="delete"
                title={t('admin.usersPage.dangerZoneView.deleteTitle')}
                description={t('admin.usersPage.dangerZoneView.messageDeleteAccount')}
              >
                <Button
                  disabled={userType !== 'consumer'}
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    setDangerZoneActionType('deleteAccount')
                  }}
                >
                  {t('admin.usersPage.dangerZoneView.buttonDeleteTitle')}
                </Button>
              </DangerZoneContainer>
              {!isAccountManager && (
                <DangerZoneContainer
                  key="address-book"
                  title={t('admin.usersPage.dangerZoneView.bookTitle')}
                  description={t('admin.usersPage.dangerZoneView.messageBook')}
                >
                  <Button
                    size="small"
                    startIcon={<AddressBookIcon />}
                    onClick={() => {
                      setDangerZoneActionType('cleanAddressBook')
                    }}
                  >
                    {t('admin.usersPage.dangerZoneView.buttonBookTitle')}
                  </Button>
                </DangerZoneContainer>
              )}
              <DangerZoneContainer
                key="login-as"
                title={t('admin.usersPage.dangerZoneView.loginAsTitle')}
                description={t('admin.usersPage.dangerZoneView.loginAsMessage')}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<KeyIcon />}
                  onClick={() => {
                    setDangerZoneActionType('loginAsUser')
                  }}
                >
                  {t('admin.usersPage.popUpView.createToken')}
                </Button>
              </DangerZoneContainer>
              {userType !== 'consumer' && userType !== 'accountManager' && (
                <DangerZoneContainer
                  key="disconnect"
                  title={t('admin.usersPage.dangerZoneView.disconnectTitle')}
                  description={t('admin.usersPage.dangerZoneView.disconnectMessage')}
                >
                  <Button size="small" startIcon={<UnlinkIcon />} onClick={disconnectUser}>
                    {t('admin.usersPage.dangerZoneView.buttonDisconnectTitle')}
                  </Button>
                </DangerZoneContainer>
              )}
            </Grid>
          </Card.Content>
        </Box>
      </Card>
      {dangerZoneActionType && (
        <UsersDangerZoneDialog
          user={user}
          actionType={dangerZoneActionType}
          open
          onClose={() => {
            setDangerZoneActionType(undefined)
          }}
          onUserRemoved={onUserRemoved}
          onUserDisconnected={onUserDisconnected}
          warningMessage={getWarningMessage}
        />
      )}
    </>
  )
}
